/**
 * Shared slide image upload section used by both LDT and SAMD.
 * Caller provides onFileAccepted and handles the post-select flow.
 * Config is derived from the current platform and workflow (useSlideScannerConfig).
 */
import { FC, useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, Paper, useTheme } from '@mui/material';
import { CloudUpload } from 'src/components/icons/cloud_upload';
import { SlideImageDownloadRow } from 'src/distribution_platforms/features/components/slide_image_download_row';
import { useSlideScannerConfig } from 'src/distribution_platforms/features/hooks/use_slide_scanner_config';
import { findMostRecentSlideImage } from 'src/distribution_platforms/features/utils/slide_image_utils';
import { UploadStatus, useUpload } from 'src/hooks/use_upload';
import {
  OrderBaseFieldsFragment,
  OrderStatus,
  UploadedFileCategory,
  UploadedFileStatus,
} from 'src/types';
import { notifyError, SpecimenTypeUtils } from 'src/utils';

export interface SlideImageSectionProps {
  order: OrderBaseFieldsFragment;
  /** Called when a valid file is accepted; caller handles dialog and upload. */
  onFileAccepted: (file: File) => void;
  /**
   * When false, completed slide files are listed without a download button.
   */
  showDownloadButton?: boolean;
}

export const SlideImageSection: FC<SlideImageSectionProps> = ({
  order,
  onFileAccepted,
  showDownloadButton = true,
}) => {
  const theme = useTheme();
  const slideConfig = useSlideScannerConfig(order.specimenType);

  const { getUploadForOrder } = useUpload();
  const { supportedExtensions, supportedFileTypesText } = slideConfig;
  const supportedFileTypeLabel =
    supportedExtensions.length === 1 ? 'file is' : 'types are';
  const supportedFileFormatLabel =
    supportedExtensions.length === 1
      ? 'the supported format'
      : 'one of the supported formats';

  const mapOverlayStatusToGraphQL = (
    overlayStatus: string,
  ): UploadedFileStatus => {
    switch (overlayStatus) {
      case UploadStatus.Queued:
      case UploadStatus.InProgress:
        return UploadedFileStatus.InProgress;
      case UploadStatus.Completed:
        return UploadedFileStatus.Complete;
      case UploadStatus.Failed:
        return UploadedFileStatus.Failed;
      default:
        return UploadedFileStatus.InProgress;
    }
  };

  // Helper function to create overlay upload node
  const createOverlayUploadNode = useCallback(
    (overlayUpload: NonNullable<ReturnType<typeof getUploadForOrder>>) => ({
      id: overlayUpload.fileId,
      filename: overlayUpload.fileName,
      category: UploadedFileCategory.SlideImage,
      status: mapOverlayStatusToGraphQL(overlayUpload.status),
      progress: overlayUpload.progress,
      createdAt: new Date().toISOString(),
    }),
    [],
  );

  const relevantSlideImage = useMemo(() => {
    // Check for overlay upload (including failed ones)
    const overlayUpload = getUploadForOrder(order.id!);
    if (overlayUpload) {
      return createOverlayUploadNode(overlayUpload);
    }

    // Fall back to order data
    return findMostRecentSlideImage(order);
  }, [getUploadForOrder, order, createOverlayUploadNode]);

  const hasUploadedFiles = relevantSlideImage !== null;
  const isUploading = useMemo(() => {
    const currentUpload = getUploadForOrder(order.id!);
    // Only block new uploads when there's an upload actually in progress
    const isClientUploadInProgress =
      currentUpload?.status === UploadStatus.InProgress;
    const isExternalUploadInProgress =
      relevantSlideImage?.status === UploadedFileStatus.InProgress;
    return isClientUploadInProgress || isExternalUploadInProgress;
  }, [getUploadForOrder, order.id, relevantSlideImage]);

  // Don't show uploader interface when order is in ReportReady status
  const shouldShowUploader = ![OrderStatus.ReportReady].includes(order.status!);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (isUploading) return;
      const file = acceptedFiles[0];
      if (!file) return;

      const fileExtension = file.name
        .toLowerCase()
        .substring(file.name.lastIndexOf('.'));
      const isSupportedExtension = supportedExtensions.some(
        (ext) => ext.toLowerCase() === fileExtension,
      );
      if (!isSupportedExtension) {
        notifyError(
          `Invalid file type. Please upload a file with ${supportedFileFormatLabel}: ${supportedFileTypesText}`,
        );
        return;
      }
      onFileAccepted(file);
    },
    [
      isUploading,
      supportedExtensions,
      supportedFileFormatLabel,
      supportedFileTypesText,
      onFileAccepted,
    ],
  );

  const onDropRejected = useCallback(() => {
    notifyError(
      `Please upload a file with ${supportedFileFormatLabel}: ${supportedFileTypesText}`,
    );
  }, [supportedFileFormatLabel, supportedFileTypesText]);

  // Map file extensions to MIME types for useDropzone
  const acceptedFileTypes = useMemo(() => {
    const extensionToMimeType: Record<string, string> = {
      '.isyntax': 'application/octet-stream',
      '.svs': 'application/octet-stream',
      '.ndpi': 'application/octet-stream',
      '.mrxs_zip': 'application/zip',
      '.zip': 'application/zip',
    };
    const acceptObject: Record<string, string[]> = {};
    supportedExtensions.forEach((ext) => {
      const mimeType = extensionToMimeType[ext];
      if (mimeType) {
        acceptObject[mimeType] = acceptObject[mimeType] || [];
        acceptObject[mimeType].push(ext);
      }
    });
    return acceptObject;
  }, [supportedExtensions]);

  const { getRootProps: getDropzoneRootProps, getInputProps } = useDropzone({
    onDrop,
    onDropRejected,
    disabled: isUploading,
    accept: acceptedFileTypes,
    noClick: isUploading,
    noKeyboard: isUploading,
  });

  // Merge our drag event handlers with dropzone's root props
  // This prevents drag events from interfering with active uploads in Safari and Firefox
  // Different browsers handle disabled dropzones differently:
  // - Chrome: Properly ignores drag events when disabled
  // - Safari: Still fires drag events, which can interfere with upload
  // - Firefox: Also fires events but doesn't handle them properly
  const getRootProps = useCallback(() => {
    const rootProps = getDropzoneRootProps();
    const originalOnDragEnter = rootProps.onDragEnter;
    const originalOnDragOver = rootProps.onDragOver;
    const originalOnDrop = rootProps.onDrop;
    return {
      ...rootProps,
      onDragEnter: (e: React.DragEvent<HTMLElement>) => {
        if (isUploading) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        if (originalOnDragEnter) originalOnDragEnter(e);
      },
      onDragOver: (e: React.DragEvent<HTMLElement>) => {
        if (isUploading) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        if (originalOnDragOver) originalOnDragOver(e);
      },
      onDrop: (e: React.DragEvent<HTMLElement>) => {
        if (isUploading) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        if (originalOnDrop) originalOnDrop(e);
      },
    };
  }, [getDropzoneRootProps, isUploading]);

  return (
    <Box>
      {shouldShowUploader && (
        <Box
          sx={{
            mt: 0.5,
            mb: 3,
          }}
        >
          <Typography
            variant='subtitle2'
            sx={{
              color: 'text.secondary',
              fontWeight: theme.typography.fontWeightLight,
              mb: 2,
            }}
          >
            {SpecimenTypeUtils.getSpecimenTypeUploadInstructions(
              order.specimenType,
            )}
          </Typography>

          <Box {...getRootProps()}>
            <Paper
              sx={{
                p: 3,
                textAlign: 'center',
                border: (theme) =>
                  `1px dashed ${
                    isUploading
                      ? theme.palette.action.disabled
                      : theme.palette.primary.main
                  }`,
                borderRadius: '6px',
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[600]
                    : theme.palette.common.white,
                cursor: isUploading ? 'not-allowed' : 'pointer',
                opacity: isUploading ? 0.6 : 1,
                '&:hover': {
                  borderColor: (theme) =>
                    isUploading
                      ? theme.palette.action.disabled
                      : theme.palette.text.primary,
                  backgroundColor: (theme) =>
                    isUploading
                      ? undefined
                      : theme.palette.mode === 'dark'
                        ? theme.palette.primary.dark
                        : theme.palette.primary.light,
                },
              }}
            >
              <input {...getInputProps()} />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <CloudUpload
                  sx={{
                    mr: 2,
                    fontSize: (theme) => theme.typography.h3.fontSize,
                    color: (theme) =>
                      isUploading
                        ? theme.palette.action.disabled
                        : theme.palette.text.secondary,
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                  }}
                >
                  <Typography
                    variant='body1'
                    color={
                      isUploading
                        ? theme.palette.action.disabled
                        : theme.palette.text.primary
                    }
                  >
                    {(() => {
                      const currentUpload = getUploadForOrder(order.id!);
                      if (currentUpload?.status === UploadStatus.InProgress) {
                        return 'Upload in progress...';
                      } else if (
                        currentUpload?.status === UploadStatus.Queued
                      ) {
                        return 'Drag and drop to replace queued file';
                      } else if (
                        currentUpload?.status === UploadStatus.Failed
                      ) {
                        return 'Drag and drop to replace failed file';
                      } else {
                        return 'Click or Drag and Drop to upload';
                      }
                    })()}
                  </Typography>
                  <Typography
                    variant='subtitle2'
                    color={
                      isUploading
                        ? theme.palette.action.disabled
                        : theme.palette.text.secondary
                    }
                  >
                    {supportedFileTypesText} {supportedFileTypeLabel} supported
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Box>
      )}
      {hasUploadedFiles && relevantSlideImage && (
        <SlideImageDownloadRow
          fileId={relevantSlideImage.id}
          filename={relevantSlideImage.filename}
          status={relevantSlideImage.status}
          showDownloadButton={showDownloadButton}
        />
      )}
    </Box>
  );
};
